import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Typography,
} from "@mui/material";

import useIsMobile from "../../shared_modules/useIsMobile";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { useTenant } from "../../hooks/useTenant";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

import SelectBarber from "./selectBarber";
import SelectService from "./selectService";
import SelectDateTime from "./selectDateTime";
import Confirmation from "./confirmation";
import SuccessfullBooking from "./successfullBooking";

import { formatTime } from "../../utils/functions";

const steps = ["Barber", "Service", "Date & Time", "Confirm"];

const pageTitles = [
  "Choose a professional",
  "Select a service",
  "Pick a date & time",
  "Confirm your booking",
];

const pageSubTitles = [
  "Pick who's cutting your hair today",
  "What are you coming in for today?",
  "Select when you'd like to come in",
  "Review everything before we confirm your appointment",
];

const buttonForwardTitles = [
  "Select your service",
  "Pick a time",
  "Review & Confirm",
  "Book Appointment",
];

const buttonBackwardTitles = ["Cancel", "Back", "Back", "Back"];

const BookAppointment = ({ onBack }) => {
  const isMobile = useIsMobile();
  const { tenant } = useTenant();
  const { api } = useAuth();
  const [searchParams] = useSearchParams();

  const scrollRef = useRef(null);
  const scrollToTop = () =>
    setTimeout(
      () => scrollRef.current?.scrollTo({ top: 0, behavior: "instant" }),
      50,
    );

  const [page, setPage] = useState(0);
  const [booked, setBooked] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [barberLockedFromUrl, setBarberLockedFromUrl] = useState(false);

  useEffect(() => {
    const barberParam = searchParams.get("barber");
    if (!barberParam) return;
    const load = async () => {
      try {
        const res = await api("/barbers/barbers.php", { method: "GET" });
        const match = (res.data.barbers || []).find(
          (b) => String(b.user_id) === String(barberParam),
        );
        if (match) {
          setSelectedBarber(match);
          setBarberLockedFromUrl(true);
          setPage(1);
        }
      } catch (_) {}
    };
    load();
  }, []);

  // ✅ Lifted from confirmation.js — drives both the right panel and Confirmation
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [tipAmount, setTipAmount] = useState(0);

  const handlePaymentMethodChange = (val) => {
    setPaymentMethod(val);
    if (val === "cash") setTipAmount(0);
  };

  const canProceed = () => {
    if (page === 0) return !!selectedBarber;
    if (page === 1) return !!selectedService;
    if (page === 2) return !!selectedSlot;
    return true;
  };

  const handleForward = () => {
    if (!canProceed()) return;
    if (page < 3) {
      setPage(page + 1);
      scrollToTop();
    }
  };

  const handleBackward = () => {
    if (page === 0) {
      onBack();
      return;
    }
    if (barberLockedFromUrl && page === 1) {
      setBarberLockedFromUrl(false);
      setPage(0);
      scrollToTop();
      return;
    }
    setPage(page - 1);
    scrollToTop();
  };

  const handleBookingSuccess = (result) => {
    setBookingResult(result);
    setBooked(true);
    scrollToTop();
  };

  if (booked) {
    return <SuccessfullBooking bookingResult={bookingResult} />;
  }

  // ── Fee calculation ────────────────────────────────────────────────────────
  const feeRate = tenant?.processing_fee_rate ?? 0.025;
  const servicePrice = selectedService ? Number(selectedService.price) : 0;
  const tip = paymentMethod === "card" ? tipAmount : 0;
  const platformFee = Math.round(servicePrice * feeRate * 100) / 100;
  const subtotal = servicePrice + tip + platformFee;
  const totalCharge = Math.round(((subtotal + 0.3) / 0.971) * 100) / 100;
  const processingFee = Math.round((totalCharge - servicePrice - tip) * 100) / 100;

  // ── Right panel shows card pricing or cash pricing based on paymentMethod ──
  const isCash = paymentMethod === "cash";

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        height: "calc(100svh - 64px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Grid container sx={{ flex: 1, overflow: "hidden", height: "100%" }}>
        {/* Left Panel */}
        <Grid
          size={isMobile ? 12 : 9}
          sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
        >
          {/* Scrollable content */}
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "30px 20px" : "30px 50px",
            }}
          >
            <Typography variant="h3">{pageTitles[page]}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
              {pageSubTitles[page]}
            </Typography>

            {/* Stepper */}
            <Box sx={{ width: "100%" }}>
              <Stepper activeStep={page} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel
                      sx={{
                        "& .MuiStepIcon-root": {
                          color: "background.default",
                          border: "1px solid",
                          borderColor: "text.primary",
                          borderRadius: "50%",
                          opacity: 0.8,
                        },
                        "& .MuiStepLabel-label": {
                          color: "text.secondary",
                          opacity: 0.8,
                          fontWeight: 200,
                        },
                        "& .MuiStepLabel-label.Mui-active": {
                          color: "text.primary",
                          fontWeight: 400,
                          opacity: 1,
                        },
                        "& .MuiStepLabel-label.Mui-completed": {
                          color: "text.primary",
                          fontWeight: 600,
                          opacity: 1,
                        },
                        "& .MuiStepIcon-text": { fill: "text.primary" },
                        "& .MuiStepIcon-root.Mui-active": {
                          color: "primary.main",
                          border: "0px",
                          opacity: 1,
                        },
                        "& .MuiStepIcon-root.Mui-active .MuiStepIcon-text": {
                          fill: "#fff",
                        },
                        "& .MuiStepIcon-root.Mui-completed": {
                          color: "primary.main",
                          border: "0px",
                          opacity: 1,
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Step content */}
            {page === 0 && (
              <SelectBarber
                selectedBarber={selectedBarber}
                setSelectedBarber={setSelectedBarber}
              />
            )}
            {page === 1 && (
              <SelectService
                barber={selectedBarber}
                selectedService={selectedService}
                setSelectedService={setSelectedService}
              />
            )}
            {page === 2 && (
              <SelectDateTime
                barber={selectedBarber}
                service={selectedService}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedSlot={selectedSlot}
                setSelectedSlot={setSelectedSlot}
              />
            )}
            {page === 3 && (
              <Confirmation
                barber={selectedBarber}
                service={selectedService}
                slot={selectedSlot}
                onSuccess={handleBookingSuccess}
                paymentMethod={paymentMethod}
                onPaymentMethodChange={handlePaymentMethodChange}
                onTipAmountChange={setTipAmount}
              />
            )}
          </Box>

          {/* Docked bottom bar */}
          <Box
            sx={{
              flexShrink: 0,
              touchAction: "none",
              borderTop: "1px solid",
              borderColor: "customDivider",
              backgroundColor: "background.default",
              px: isMobile ? "20px" : "50px",
              pt: "16px",
              pb: isMobile
                ? "calc(16px + env(safe-area-inset-bottom, 20px))"
                : "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {page > 0 || (barberLockedFromUrl && page === 1) ? (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBackward}
              >
                <ArrowRightAltIcon sx={{ transform: "rotate(180deg)" }} />
                {buttonBackwardTitles[page]}
              </Button>
            ) : (
              <Box />
            )}

            {page < 3 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleForward}
                disabled={!canProceed()}
              >
                {buttonForwardTitles[page]}
                <ArrowRightAltIcon />
              </Button>
            )}
          </Box>
        </Grid>

        {/* Right Panel — Order Summary */}
        <Grid
          size={3}
          sx={{
            backgroundColor: "offWhite",
            borderLeft: "0.1px solid",
            borderColor: "customDivider",
            display: isMobile ? "none" : "block",
          }}
        >
          <Box sx={{ padding: isMobile ? "30px 20px" : "30px 24px" }}>
            <Typography variant="h3">Your Order</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
              Step {page + 1} of 4
            </Typography>

            <Box
              sx={{
                backgroundColor: "primary.main",
                borderRadius: "16px",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: "secondary.contrastText", opacity: 0.7 }}
              >
                Summary
              </Typography>

              {[
                { label: "Service", value: selectedService?.name || "—" },
                {
                  label: "Duration",
                  value: selectedService
                    ? `${selectedService.duration_minutes} min`
                    : "—",
                },
                {
                  label: "Barber",
                  value: selectedBarber
                    ? `${selectedBarber.first_name} ${selectedBarber.last_name}`
                    : "—",
                },
                {
                  label: "Date",
                  value: selectedSlot
                    ? new Date(
                        selectedSlot.start_utc.replace(" ", "T") + "Z",
                      ).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })
                    : "—",
                },
                {
                  label: "Time",
                  value: selectedSlot ? formatTime(selectedSlot.start) : "—",
                },
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: "16px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(254,252,247,0.6)" }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "primary.contrastText",
                        fontWeight: 500,
                        textAlign: "right",
                        maxWidth: "60%",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                  <Divider
                    sx={{ borderColor: "rgba(254,252,247,0.15)", mt: 1 }}
                  />
                </Box>
              ))}

              {selectedService ? (
                <>
                  {/* ── Service price row — always shown ── */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: "16px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(254,252,247,0.6)" }}
                    >
                      Service price
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "primary.contrastText", fontWeight: 500 }}
                    >
                      ${servicePrice.toFixed(2)}
                    </Typography>
                  </Box>
                  <Divider
                    sx={{ borderColor: "rgba(254,252,247,0.15)", mt: 1 }}
                  />

                  {/* ── Tip — card only, when selected ── */}
                  {!isCash && tip > 0 && (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mt: "16px" }}>
                        <Typography variant="body2" sx={{ color: "rgba(254,252,247,0.6)" }}>
                          Tip for barber
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#C4984D", fontWeight: 600 }}>
                          +${tip.toFixed(2)}
                        </Typography>
                      </Box>
                      <Divider sx={{ borderColor: "rgba(254,252,247,0.15)", mt: 1 }} />
                    </>
                  )}

                  {/* ── Processing fee — card only ── */}
                  {!isCash && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: "16px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "rgba(254,252,247,0.6)" }}
                        >
                          Processing fee
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "primary.contrastText",
                            fontWeight: 500,
                          }}
                        >
                          ${processingFee.toFixed(2)}
                        </Typography>
                      </Box>
                      <Divider
                        sx={{ borderColor: "rgba(254,252,247,0.15)", mt: 1 }}
                      />
                    </>
                  )}

                  {/* ── Total row ── */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: "24px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(254,252,247,0.6)" }}
                    >
                      {isCash ? "Due at appointment" : "Total"}
                    </Typography>
                    <Typography variant="h3" sx={{ color: "secondary.main" }}>
                      {isCash
                        ? `$${servicePrice.toFixed(2)} cash`
                        : `$${totalCharge.toFixed(2)}`}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: "40px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(254,252,247,0.6)" }}
                  >
                    Subtotal
                  </Typography>
                  <Typography variant="h3" sx={{ color: "secondary.main" }}>
                    —
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BookAppointment;
